import { useCallback, useEffect, useState } from 'react';
import { Check, Eye, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import {
  contactService,
  subjectLabel,
  type ContactMessageRow,
  type ContactReadStatus,
} from '../services/contactService';
import { formatDateTime } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

export function ContactMessagesPage() {
  const [status, setStatus] = useState<ContactReadStatus>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<ContactMessageRow[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<ContactMessageRow | null>(null);
  const limit = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await contactService.list({ page, limit, status });
      setMessages(res.data ?? []);
      setTotalPages(Math.max(1, res.totalPages || 1));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت پیام‌ها');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (row: ContactMessageRow) => {
    setSelected(row);
    setDetailOpen(true);
    if (!row.isRead) {
      try {
        await contactService.markRead(row.id);
        setMessages((prev) => prev.map((m) => (m.id === row.id ? { ...m, isRead: true } : m)));
        setSelected({ ...row, isRead: true });
      } catch {
        /* non-blocking */
      }
    }
  };

  const markRead = async (id: string) => {
    try {
      await contactService.markRead(id);
      toast.success('پیام خوانده شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('این پیام حذف شود؟')) return;
    try {
      await contactService.remove(id);
      toast.success('پیام حذف شد');
      setDetailOpen(false);
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در حذف');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-2xl">پیام‌های تماس</h1>
          <p className="mt-1 text-sm text-slate-500">پیام‌های ارسالی از فرم تماس با ما</p>
        </div>
        <div className="flex rounded-xl border border-slate-200 bg-white p-1">
          {([
            ['', 'همه'],
            ['unread', 'خوانده‌نشده'],
            ['read', 'خوانده‌شده'],
          ] as const).map(([value, label]) => (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => {
                setStatus(value);
                setPage(1);
              }}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                status === value ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="admin-table-scroll">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-right font-semibold">#</th>
                <th className="px-4 py-3 text-right font-semibold">نام</th>
                <th className="px-4 py-3 text-right font-semibold">شماره</th>
                <th className="px-4 py-3 text-right font-semibold">موضوع</th>
                <th className="px-4 py-3 text-right font-semibold">تاریخ</th>
                <th className="px-4 py-3 text-right font-semibold">وضعیت</th>
                <th className="px-4 py-3 text-right font-semibold">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : messages.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                    پیامی یافت نشد
                  </td>
                </tr>
              ) : (
                messages.map((row, index) => (
                  <tr key={row.id} className={`border-t border-slate-100 ${!row.isRead ? 'bg-indigo-50/40' : ''}`}>
                    <td className="px-4 py-3 tabular-nums">{rowNumber(index, page, limit)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{row.name}</td>
                    <td className="px-4 py-3 tabular-nums" dir="ltr">{row.phone || '—'}</td>
                    <td className="px-4 py-3">{subjectLabel(row.subject)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          row.isRead ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {row.isRead ? 'خوانده‌شده' : 'جدید'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void openDetail(row)}
                          className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-indigo-600"
                          title="مشاهده"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {!row.isRead && (
                          <button
                            type="button"
                            onClick={() => void markRead(row.id)}
                            className="rounded-lg p-2 text-slate-500 hover:bg-green-50 hover:text-green-600"
                            title="علامت خوانده‌شده"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void remove(row.id)}
                          className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

      <Modal
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelected(null);
        }}
        title="جزئیات پیام"
      >
        {selected ? (
          <div className="space-y-4 text-sm">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-slate-500">نام</p>
                <p className="font-semibold text-slate-900">{selected.name}</p>
              </div>
              <div>
                <p className="text-slate-500">شماره تماس</p>
                <p className="font-semibold tabular-nums text-slate-900" dir="ltr">{selected.phone || '—'}</p>
              </div>
              <div>
                <p className="text-slate-500">موضوع</p>
                <p className="font-semibold text-slate-900">{subjectLabel(selected.subject)}</p>
              </div>
              <div>
                <p className="text-slate-500">تاریخ</p>
                <p className="font-semibold text-slate-900">{formatDateTime(selected.createdAt)}</p>
              </div>
            </div>
            <div>
              <p className="mb-2 text-slate-500">متن پیام</p>
              <p className="whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-4 leading-7 text-slate-800">
                {selected.message}
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
              {!selected.isRead && (
                <button
                  type="button"
                  onClick={() => void markRead(selected.id)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Check className="h-4 w-4" />
                  علامت خوانده‌شده
                </button>
              )}
              <button
                type="button"
                onClick={() => void remove(selected.id)}
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
                حذف
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
